/* Grainova wishlist (Claude, round 7, 25 Sep 2026). Replaces the Ultimate Wishlist app with a theme-native wishlist
   in the v2 design.
   - Saved products live in this browser (localStorage "gv2:wishlist", newest first, max 60 handles).
   - [data-gv2-wl-toggle][data-handle] buttons (product cards, product page) save / remove a product.
   - [data-gv2-wl-count] shows the number of saved products (header heart).
   - [data-gv2-wl-page] (/pages/wishlist) lists the saved products with Add to cart, Remove and Add all to cart.
   Everything degrades gracefully: without storage the buttons still toggle for the current page view. */
(() => {
  if (window.GV2Wishlist) return;
  const KEY = 'gv2:wishlist';
  const MAX = 60;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let memory = [];

  const read = () => {
    try {
      const v = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(v) ? v.filter((h) => typeof h === 'string' && h) : [];
    } catch (e) { return memory.slice(); }
  };
  const write = (list) => {
    memory = list.slice(0, MAX);
    try { localStorage.setItem(KEY, JSON.stringify(memory)); } catch (e) { /* private mode: keep in memory */ }
  };

  const W = window.GV2Wishlist = {
    list: read,
    has: (h) => read().includes(h),
    add(h) { if (!h) return; write([h, ...read().filter((x) => x !== h)]); this.sync(); },
    remove(h) { write(read().filter((x) => x !== h)); this.sync(); },
    toggle(h) { if (this.has(h)) { this.remove(h); return false; } this.add(h); return true; },
    sync() {
      const list = read();
      document.querySelectorAll('[data-gv2-wl-toggle][data-handle]').forEach((b) => {
        const on = list.includes(b.dataset.handle);
        b.setAttribute('aria-pressed', String(on));
        b.classList.toggle('is-saved', on);
        const t = b.dataset.title || 'this product';
        if (b.hasAttribute('data-gv2-wl-icon')) b.setAttribute('aria-label', on ? `Remove ${t} from wishlist` : `Save ${t} to wishlist`);
        const label = b.querySelector('[data-gv2-wl-label]');
        if (label) label.textContent = on ? 'Saved to wishlist' : 'Add to wishlist';
      });
      document.querySelectorAll('[data-gv2-wl-count]').forEach((el) => { el.textContent = list.length; el.hidden = list.length === 0; });
      document.querySelectorAll('[data-gv2-wl-link]').forEach((a) => a.setAttribute('aria-label', list.length ? `Wishlist, ${list.length} saved` : 'Wishlist'));
    },
  };

  /* small toast: "Saved to your wishlist · View" */
  let toast; let toastTimer;
  const say = (msg, withLink) => {
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'gv2-wl-toast'; toast.setAttribute('role', 'status'); toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.innerHTML = '';
    const span = document.createElement('span'); span.textContent = msg; toast.appendChild(span);
    if (withLink && !document.querySelector('[data-gv2-wl-page]')) {
      const a = document.createElement('a'); a.href = `${root}pages/wishlist`; a.textContent = 'View'; toast.appendChild(a);
    }
    toast.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-on'), 3000);
  };

  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-gv2-wl-toggle][data-handle]');
    if (!b) return;
    e.preventDefault(); e.stopPropagation();
    const on = W.toggle(b.dataset.handle);
    if (on) { b.classList.remove('is-pop'); void b.offsetWidth; b.classList.add('is-pop'); }
    say(on ? 'Saved to your wishlist' : 'Removed from your wishlist', on);
    if (!on && document.querySelector('[data-gv2-wl-page]')) renderPage();
  });
  window.addEventListener('storage', (e) => { if (e.key === KEY) { W.sync(); if (document.querySelector('[data-gv2-wl-page]')) renderPage(); } });

  /* ---------- /pages/wishlist ---------- */
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const cache = {};
  const fetchProduct = (h) => {
    if (!cache[h]) {
      cache[h] = fetch(`${root}products/${encodeURIComponent(h)}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null)).catch(() => undefined);
    }
    return cache[h];
  };
  const imgUrl = (src, w) => {
    if (!src) return '';
    if (src.startsWith('data:')) return src;
    const u = src.startsWith('//') ? `https:${src}` : src;
    return u + (u.includes('?') ? '&' : '?') + `width=${w}`;
  };

  let rendering = 0;
  async function renderPage() {
    const page = document.querySelector('[data-gv2-wl-page]');
    if (!page) return;
    const run = ++rendering;
    const fmt = page.dataset.moneyFormat;
    const money = (cents) => { const n = cents / 100; const s = n.toLocaleString('en-IN', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 }); return fmt ? fmt.replace(/\{\{[^}]*\}\}/, s) : `₹${s}`; };
    const grid = page.querySelector('[data-gv2-wl-grid]');
    const empty = page.querySelector('[data-gv2-wl-empty]');
    const bar = page.querySelector('[data-gv2-wl-bar]');
    const countEl = page.querySelector('[data-gv2-wl-total]');
    const handles = read();
    page.setAttribute('aria-busy', 'true');
    if (!handles.length) {
      grid.innerHTML = ''; empty.hidden = false; bar.hidden = true; page.removeAttribute('aria-busy'); return;
    }
    if (!grid.children.length) grid.innerHTML = handles.map(() => '<li class="gv2-wl-card gv2-wl-card--skeleton" aria-hidden="true"><span></span><span></span><span></span></li>').join('');
    const results = await Promise.all(handles.map(fetchProduct));
    if (run !== rendering) return;
    const products = [];
    results.forEach((p, i) => {
      if (p === null) { write(read().filter((x) => x !== handles[i])); return; } // product no longer exists
      if (p) products.push(p);
    });
    W.sync();
    empty.hidden = products.length > 0;
    bar.hidden = products.length === 0;
    countEl.textContent = products.length === 1 ? '1 product' : `${products.length} products`;
    grid.innerHTML = products.map((p) => {
      const v = p.variants.find((x) => x.available) || p.variants[0];
      const multi = p.variants.length > 1;
      const opts = multi ? p.variants.map((x) => `<option value="${x.id}" data-price="${x.price}" data-compare="${x.compare_at_price || 0}" ${x.id === v.id ? 'selected' : ''} ${x.available ? '' : 'disabled'}>${esc(x.title)}${x.available ? '' : ' – sold out'}</option>`).join('') : '';
      const img = p.featured_image;
      return `<li class="gv2-wl-card" data-handle="${esc(p.handle)}">
        <div class="gv2-wl-card__media">
          <a href="${esc(p.url)}" tabindex="-1" aria-hidden="true">${img ? `<img src="${esc(imgUrl(img, 400))}" srcset="${esc(imgUrl(img, 300))} 300w, ${esc(imgUrl(img, 600))} 600w" sizes="(max-width: 699px) 46vw, 260px" alt="" loading="lazy" width="400" height="400">` : ''}</a>
          <button type="button" class="gv2-wl-heart is-saved" data-gv2-wl-toggle data-gv2-wl-icon data-handle="${esc(p.handle)}" data-title="${esc(p.title)}" aria-pressed="true" aria-label="Remove ${esc(p.title)} from wishlist">${HEART}</button>
        </div>
        <div class="gv2-wl-card__body">
          <a class="gv2-wl-card__title" href="${esc(p.url)}">${esc(p.title)}</a>
          ${multi ? `<label class="sr-only" for="gv2-wl-v-${p.id}">Size</label><select id="gv2-wl-v-${p.id}" class="gv2-wl-card__variant" data-gv2-wl-variant>${opts}</select>` : (v.title && v.title !== 'Default Title' ? `<span class="gv2-wl-card__size">${esc(v.title)}</span>` : '')}
          <p class="gv2-wl-card__price"><span data-gv2-wl-price>${money(v.price)}</span><s data-gv2-wl-compare ${v.compare_at_price > v.price ? '' : 'hidden'}>${money(v.compare_at_price || 0)}</s></p>
          <button type="button" class="gv2-wl-card__add" data-gv2-wl-add data-variant="${v.id}" ${v.available ? '' : 'disabled'}>${v.available ? 'Add to cart' : 'Sold out'}</button>
        </div>
      </li>`;
    }).join('');
    page.removeAttribute('aria-busy');
  }

  const HEART = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20.5s-7.5-4.6-9.2-9.3C1.7 8 3.6 4.5 7.1 4.5c2 0 3.6 1.1 4.9 2.9 1.3-1.8 2.9-2.9 4.9-2.9 3.5 0 5.4 3.5 4.3 6.7-1.7 4.7-9.2 9.3-9.2 9.3z"/></svg>';

  const openCart = async (cart) => {
    document.querySelectorAll('cart-count').forEach((el) => { el.textContent = cart.item_count; });
    document.querySelectorAll('cart-dot').forEach((el) => { el.classList.toggle('is-visible', cart.item_count > 0); el.textContent = cart.item_count; });
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    const d = document.getElementById('cart-drawer');
    if (d && typeof d.show === 'function') setTimeout(() => d.show(), 250);
  };
  const addItems = async (items, btn) => {
    const label = btn.textContent;
    btn.disabled = true; btn.setAttribute('aria-busy', 'true'); btn.textContent = 'Adding…';
    try {
      const r = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.description || data.message || 'Could not add to cart.');
      const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
      btn.textContent = 'Added ✓';
      say(items.length > 1 ? `${items.length} products added to your cart` : 'Added to your cart', false);
      openCart(cart);
    } catch (err) {
      btn.textContent = label; say(err.message || 'Could not add to cart. Please try again.', false);
    } finally {
      btn.removeAttribute('aria-busy');
      setTimeout(() => { btn.disabled = false; if (btn.textContent === 'Added ✓') btn.textContent = label; }, 1800);
    }
  };

  document.addEventListener('change', (e) => {
    const s = e.target.closest('[data-gv2-wl-variant]');
    if (!s) return;
    const card = s.closest('.gv2-wl-card');
    const o = s.selectedOptions[0];
    const page = document.querySelector('[data-gv2-wl-page]');
    const fmt = page && page.dataset.moneyFormat;
    const money = (cents) => { const n = cents / 100; const t = n.toLocaleString('en-IN', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 }); return fmt ? fmt.replace(/\{\{[^}]*\}\}/, t) : `₹${t}`; };
    card.querySelector('[data-gv2-wl-price]').textContent = money(+o.dataset.price);
    const c = card.querySelector('[data-gv2-wl-compare]'); const cmp = +o.dataset.compare;
    c.hidden = !(cmp > +o.dataset.price); c.textContent = money(cmp);
    card.querySelector('[data-gv2-wl-add]').dataset.variant = s.value;
  });
  document.addEventListener('click', (e) => {
    const add = e.target.closest('[data-gv2-wl-add]');
    if (add) { addItems([{ id: +add.dataset.variant, quantity: 1 }], add); return; }
    const all = e.target.closest('[data-gv2-wl-addall]');
    if (all) {
      const items = Array.from(document.querySelectorAll('[data-gv2-wl-add]:not([disabled])')).map((b) => ({ id: +b.dataset.variant, quantity: 1 }));
      if (items.length) addItems(items, all); else say('Everything here is sold out right now.', false);
    }
  });

  const start = () => { W.sync(); renderPage(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  // product cards added later (quick buy, recommendations, infinite scroll) pick up the saved state
  new MutationObserver((muts) => {
    if (muts.some((m) => Array.from(m.addedNodes).some((n) => n.nodeType === 1 && (n.matches('[data-gv2-wl-toggle]') || n.querySelector('[data-gv2-wl-toggle]'))))) W.sync();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
